import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Heart, Camera, QrCode } from 'lucide-react';
import invisalignQrCode from '../../assets/InvisalignQRCode.jpg';

export function ServicePartnerInstruction() {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-teal-50 via-white to-green-50 py-20 px-6">
      {/* Background SVG Pattern */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="100%" 
          height="100%" 
          className="text-teal-200"
        >
          <defs>
            <pattern id="logoPattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
              <path 
                d="M50 0 L75 25 L50 50 L25 25 Z" 
                fill="currentColor" 
                fillOpacity="0.1"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#logoPattern)" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 items-center gap-12 relative z-10">
        {/* Left Content */}
        <motion.div 
          className="space-y-6"
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl font-bold text-gray-900 relative">
            <span className="text-teal-600">See Your Future Smile</span> – Instantly!
            <div className="absolute -top-2 -left-8 text-teal-300 opacity-30">
              <Heart size={40} strokeWidth={1} />
            </div>
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed">
            Curious how your smile could look with Invisalign? Our advanced smile simulator gives you a personalized preview — instantly and commitment-free.
          </p>
          <ul className="space-y-3 text-gray-600">
            {[
              { icon: <Camera className="text-teal-500" />, text: "Scan the QR code with your smartphone" },
              { icon: <Heart className="text-green-500" />, text: "Visualize your potential transformation" },
              { icon: <QrCode className="text-blue-500" />, text: "Try it before you commit to treatment" },
              { icon: <Heart className="text-teal-500" />, text: "Fast, free, and fun to explore" }
            ].map((item, index) => (
              <li 
                key={index} 
                className="flex items-center gap-3 hover:text-teal-700 transition-colors"
              >
                {item.icon}
                {item.text}
              </li>
            ))}
          </ul>
          <motion.a
            href="/schedule-appointment"
            className="inline-block mt-6 bg-teal-600 text-white px-6 py-3 rounded-lg shadow hover:bg-teal-700 transition"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Schedule Your Invisalign Consultation
          </motion.a>
        </motion.div>

        {/* Right: QR Code + Step-by-step instructions */}
        <motion.div 
          className="w-full max-w-sm mx-auto space-y-6 text-center"
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div 
            className="bg-white p-6 rounded-xl shadow-lg border border-teal-100 transition-all duration-300"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <motion.img
              src={invisalignQrCode}
              alt="Scan to see your future smile"
              className="w-full h-auto mb-4 rounded-lg"
              animate={{ 
                scale: isHovered ? 1.05 : 1,
                rotate: isHovered ? 2 : 0
              }}
              transition={{ duration: 0.3 }}
            />
            <p className="text-sm text-gray-600">Scan the QR code to see your smile transformation</p>
          </div>

          {/* Step-by-step */}
          <div className="text-left space-y-4">
            {[
              "Open your phone's camera app.",
              "Point it at the QR code so it's visible in the viewfinder.",
              "Tap the notification to open the simulation in your browser."
            ].map((step, index) => (
              <div key={index} className="flex items-start gap-3">
                <motion.span 
                  className="text-white bg-teal-500 rounded-full w-8 h-8 flex items-center justify-center font-bold"
                  initial={{ opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.2 }}
                >
                  {`0${index + 1}`}
                </motion.span>
                <p className="text-gray-700">{step}</p>
              </div>
            ))}
            <p className="text-sm text-gray-500 mt-2">
              *For some Android phones, a QR code app may be required.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default ServicePartnerInstruction;